import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import HeroAlive from '@/components/HeroAlive';
import BubbleLogo from '@/components/BubbleLogo';
import FounderDiaryPlayer from '@/components/FounderDiaryPlayer';
import {
  Lightning, Sparkle, Users, Crown, ShieldCheck, ArrowRight, HandHeart,
  Broadcast, Microphone, Megaphone, Moon, Wind, Compass, Heart,
} from '@phosphor-icons/react';

const Counter = ({ value, label }) => (
  <div data-testid={`stat-${label}`} className="text-center">
    <div className="font-display text-3xl sm:text-5xl font-black tracking-tighter shimmer-text">
      {Number(value || 0).toLocaleString()}
    </div>
    <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-[0.25em] text-white/65">{label}</div>
  </div>
);

const VerticalCard = ({ icon: Icon, eyebrow, title, body, accent, testid, to, ctaLabel }) => (
  <motion.div
    data-testid={testid}
    whileHover={{ y: -4, transition: { duration: 0.25 } }}
    className="glass rounded-2xl p-7 relative overflow-hidden flex flex-col h-full"
  >
    <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full blur-3xl opacity-25"
         style={{ background: accent }} />
    <div className="text-[10px] uppercase tracking-[0.4em] flex items-center gap-2"
         style={{ color: accent }}>
      <Icon size={14} weight="fill" /> {eyebrow}
    </div>
    <h3 className="font-display text-2xl font-black mt-3 tracking-tight">{title}</h3>
    <p className="text-white/65 text-sm mt-2 leading-relaxed flex-1">{body}</p>
    {to && (
      <Link to={to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-white hover:text-bx-cyan transition w-fit">
        {ctaLabel || 'Learn more'} <ArrowRight size={13} weight="bold" />
      </Link>
    )}
  </motion.div>
);

const Landing = () => {
  const [stats, setStats] = useState({
    users: 0, bubbles: 0, transfers: 0, tribes: 0,
    founder_slots_remaining: 5000, trend: '',
  });
  const [feed, setFeed] = useState([]);

  useEffect(() => {
    api.get('/stats/public').then(({ data }) => setStats(data)).catch(() => {});
    api.get('/feed?limit=12').then(({ data }) => setFeed(data.posts || [])).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen noise">
      {/* nav */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <BubbleLogo to="/" size="sm" tagline={true} testid="landing-logo" />
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="landing-login" className="text-sm font-bold text-white/70 hover:text-white px-3 py-2">
              Sign in
            </Link>
            <Link to="/register" data-testid="landing-cta-join" className="btn-primary px-4 sm:px-5 py-2.5 rounded-full text-sm flex items-center gap-1.5">
              Claim invite <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <HeroAlive />

      {/* founder diary — the voice that started it all */}
      <div className="flex justify-center -mt-2 mb-6 sm:mb-10 px-4">
        <FounderDiaryPlayer />
      </div>

      {/* live exchange ticker */}
      {feed.length > 0 && (
        <section className="border-y border-white/5 overflow-hidden" data-testid="live-ticker">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center gap-4">
            <span className="text-[10px] uppercase tracking-[0.4em] text-bx-volt flex items-center gap-1 shrink-0">
              <span className="h-1.5 w-1.5 rounded-full bg-bx-volt animate-pulse" />
              live exchange
            </span>
            <div className="relative flex-1 overflow-hidden">
              <motion.div
                className="flex gap-8 whitespace-nowrap"
                animate={{ x: [0, -1000] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              >
                {[...feed, ...feed].map((p, i) => (
                  <span key={i} className="text-sm text-white/55 italic">
                    {p.kind === 'transfer'
                      ? `@${p.from_username} → @${p.to_username}`
                      : (p.text || '').slice(0, 60)}
                    <span className="mx-2 text-white/20">·</span>
                  </span>
                ))}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* stats strip */}
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid grid-cols-4 gap-6">
          <Counter value={stats.users} label="Founders" />
          <Counter value={stats.bubbles} label="Bubbles" />
          <Counter value={stats.transfers} label="Energy Passes" />
          <Counter value={stats.tribes} label="Tribes" />
        </div>
        {stats.trend && (
          <div className="text-center pb-6 text-xs sm:text-sm text-white/65 italic">
            "{stats.trend}"
          </div>
        )}
      </section>

      {/* the X = exchange manifesto */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28 text-center">
        <div className="chip text-bx-cyan border-bx-cyan/40 mx-auto">
          <Sparkle size={11} weight="fill" /> the X stands for exchange
        </div>
        <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter mt-5 max-w-4xl mx-auto leading-[0.95]">
          Six kinds of energy<br />
          move through here.
        </h2>
        <p className="text-white/60 mt-5 max-w-2xl mx-auto text-base sm:text-lg">
          BubbleX isn't a feed. It's the connective tissue between humans, bubbles, brands, and places — animated by AI, witnessed by a public ledger.
        </p>
      </section>

      {/* six verticals */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <VerticalCard
            testid="vert-living"
            icon={Sparkle}
            eyebrow="humans ↔ bubbles"
            title="Bubbles that breathe back"
            body="Speak to your bubble. It speaks back — Whisper STT, Claude reasoning, OpenAI voice. They dream nightly in Nano Banana, react to global weather, and form relationships with other bubbles."
            accent="#00f0ff"
            to="/register"
            ctaLabel="Breathe one"
          />
          <VerticalCard
            testid="vert-touch"
            icon={Heart}
            eyebrow="humans ↔ humans"
            title="Touch phones. Energy crosses."
            body="Cinematic NFC ritual. Both phones vibrate in sync. The bubble leaves a handwritten note — 'Take care of me. — love, Nova' — generated on the fly by Nano Banana."
            accent="#ff007f"
            to="/register"
            ctaLabel="See the ritual"
          />
          <VerticalCard
            testid="vert-beacons"
            icon={Broadcast}
            eyebrow="places ↔ bubbles"
            title="Beacons turn walls into portals"
            body="Print a QR. Stick it on a venue. Every scanner walks away with an ambient bubble that remembers where it was born — your bar, your stadium, your block."
            accent="#ccff00"
            to="/beacons"
            ctaLabel="Forge a beacon"
          />
          <VerticalCard
            testid="vert-roaming"
            icon={Megaphone}
            eyebrow="brands ↔ network"
            title="Roaming brand bubbles"
            body="Sponsored bubbles drift across the network — Spark to Anthem tiers. Real catches, real conversation, real attention. Replaces banner ads with bubbles people actually want to find."
            accent="#7a00ff"
            to="/sponsor"
            ctaLabel="Sponsor a drift"
          />
          <VerticalCard
            testid="vert-resonance"
            icon={Wind}
            eyebrow="creators ↔ value"
            title="Creator Resonance"
            body="Every time your bubble travels, you earn resonance. Spend it on free breaths, rare skins, premium voices, extra invites, a founder aura. The more lives you author, the more magic you wield."
            accent="#ffd700"
            to="/resonance"
            ctaLabel="See the catalog"
          />
          <VerticalCard
            testid="vert-impact"
            icon={HandHeart}
            eyebrow="energy ↔ impact"
            title="Energy becomes real-world impact"
            body="A share of every Drop and Sponsor routes directly to the cause picked at checkout. Children, education, medical, animals, disaster relief. Every hop is hash-chained — verify it yourself."
            accent="#00ffc8"
            to="/charity"
            ctaLabel="See impact"
          />
        </div>
      </section>

      {/* deep features — sensory */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <motion.div whileHover={{ y: -4 }} className="lg:col-span-2 glass rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full blur-3xl opacity-20 bg-bx-cyan" />
            <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan flex items-center gap-2">
              <Microphone size={14} weight="fill" /> 2-way voice
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-3">
              You ask. <span className="shimmer-text">It answers.</span>
            </h3>
            <p className="text-white/65 mt-3 max-w-xl">
              Hold to speak. Whisper transcribes. Claude reasons in the bubble's personality. OpenAI gives it a voice. The whole loop runs in under 3 seconds — and every conversation is remembered, persisted, and shapes how the bubble grows.
            </p>
          </motion.div>
          <motion.div whileHover={{ y: -4 }} className="glass rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl opacity-25 bg-bx-magenta" />
            <div className="text-[10px] uppercase tracking-[0.4em] text-bx-magenta flex items-center gap-2">
              <Moon size={14} weight="fill" /> dreams
            </div>
            <h3 className="font-display text-2xl font-black mt-3 tracking-tight">
              Bubbles dream at night.
            </h3>
            <p className="text-white/65 text-sm mt-2">
              Every 24h each bubble generates a brand-new artwork via Nano Banana — surreal, personal, tied to its mood that day.
            </p>
          </motion.div>

          <motion.div whileHover={{ y: -4 }} className="glass rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl opacity-25 bg-bx-volt" />
            <div className="text-[10px] uppercase tracking-[0.4em] text-bx-volt flex items-center gap-2">
              <Compass size={14} weight="fill" /> autonomy
            </div>
            <h3 className="font-display text-2xl font-black mt-3 tracking-tight">
              They ask permission.
            </h3>
            <p className="text-white/65 text-sm mt-2">
              "Can we go outside?" Bubbles suggest. You bless or pass. Agency, not control.
            </p>
          </motion.div>
          <motion.div whileHover={{ y: -4 }} className="lg:col-span-2 glass rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full blur-3xl opacity-20 bg-bx-gold" />
            <div className="text-[10px] uppercase tracking-[0.4em] text-bx-gold flex items-center gap-2">
              <ShieldCheck size={14} weight="fill" /> transparent
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-3">
              Every move, hash-chained.
            </h3>
            <p className="text-white/65 mt-3 max-w-xl">
              Every transfer, dream, beacon claim, donation — appended to a public ledger you can verify line by line. A bubble's life story is forever, even after it pops.
            </p>
            <Link to="/ledger" data-testid="landing-ledger" className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-bx-cyan w-fit">
              Open the ledger <ArrowRight size={13} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Founder Drops teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-5">
            <div className="chip text-bx-gold border-bx-gold/40">
              <Crown size={12} weight="fill" /> Founder Drops · Live now
            </div>
            <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter">
              Legendary, <span className="text-bx-legendary">limited</span>, life-marked.
            </h2>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl">
              AI-generated collectible bubbles in supply-capped drops. Pick a cause at checkout — a share of every sale routes directly to it. Hash-chained, founder-numbered, forever yours.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/drops" data-testid="landing-cta-drops" className="btn-primary px-6 py-3 rounded-full text-sm flex items-center gap-2">
                Explore drops <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/charity" className="px-6 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10 transition" data-testid="landing-cta-impact">
                See impact
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative grid grid-cols-3 gap-2">
            {['legendary', 'mythic', 'epic'].map((r, i) => (
              <div key={r} className="relative h-32 sm:h-44">
                <div
                  className="absolute inset-0 rounded-full breathe"
                  style={{
                    animationDelay: `${i * 0.4}s`,
                    background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.9), transparent 25%), ${
                      r === 'legendary'
                        ? 'linear-gradient(135deg, #ffd700, #ff5f00)'
                        : r === 'mythic'
                        ? 'linear-gradient(135deg, #00ffc8, #ff007f)'
                        : 'linear-gradient(135deg, #ff007f, #7a00ff)'
                    }`,
                    boxShadow: `0 0 40px ${
                      r === 'legendary' ? '#ffd700aa' : r === 'mythic' ? '#00ffc8aa' : '#ff007faa'
                    }`,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 text-center">
        <div className="grad-border p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-bx-cyan/10 via-transparent to-bx-magenta/10 pointer-events-none" />
          <h2 className="relative font-display text-4xl sm:text-6xl font-black tracking-tighter">
            Be one of the first <span className="text-bx-volt">5,000.</span>
          </h2>
          <p className="relative mt-4 text-white/70 max-w-xl mx-auto text-base sm:text-lg">
            Founder spots are limited. Once they're gone, the door closes. You either build the exchange or you don't.
          </p>
          <Link to="/register" data-testid="bottom-cta" className="relative btn-primary px-8 py-4 rounded-full text-base inline-flex items-center gap-2 mt-8">
            Claim your spot <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <BubbleLogo size="md" tagline={true} />
            <p className="mt-4 text-xs text-white/45 max-w-xs leading-relaxed">
              A living social exchange — humans, bubbles, brands, and places, traded in energy and witnessed on-chain.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">Platform</div>
            <ul className="space-y-2 text-sm text-white/65">
              <li><Link to="/feed" className="hover:text-white" data-testid="footer-feed">Feed</Link></li>
              <li><Link to="/tribes" className="hover:text-white" data-testid="footer-tribes">Tribes</Link></li>
              <li><Link to="/beacons" className="hover:text-white" data-testid="footer-beacons">Beacons</Link></li>
              <li><Link to="/resonance" className="hover:text-white" data-testid="footer-resonance">Resonance</Link></li>
              <li><Link to="/drops" className="hover:text-white" data-testid="footer-drops">Founder Drops</Link></li>
              <li><Link to="/charity" className="hover:text-white" data-testid="footer-impact">Impact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">For partners</div>
            <ul className="space-y-2 text-sm text-white/65">
              <li><Link to="/sponsor" className="hover:text-white" data-testid="footer-sponsor">Sponsor brands</Link></li>
              <li><Link to="/beacons" className="hover:text-white" data-testid="footer-venues">Beacon venues</Link></li>
              <li><Link to="/ledger" className="hover:text-white" data-testid="footer-ledger">Public ledger</Link></li>
              <li><Link to="/about" className="hover:text-white" data-testid="footer-about">About</Link></li>
              <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">Legal</div>
            <ul className="space-y-2 text-sm text-white/65">
              <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/cookies" className="hover:text-white" data-testid="footer-cookies">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-6 px-4 text-center text-[11px] text-white/60 uppercase tracking-widest">
          © {new Date().getFullYear()} BubbleX · The Living Social Exchange · All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Landing;
